import { useEffect, useState, FormEvent } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Provider {
  id: string; kind: string; providerKey: string; displayName?: string; modelId?: string;
  status: string; secretFingerprint?: string; createdAt: string;
}

const PRESETS = [
  { label: "Google Gemini (imagen)", kind: "image", providerKey: "gemini", endpointAllowlistKey: "gemini-v1beta", modelDefault: "gemini-2.0-flash-preview-image-generation" },
  { label: "BFL / FLUX (imagen)", kind: "image", providerKey: "bfl", endpointAllowlistKey: "bfl-v1", modelDefault: "flux-pro-1.1" },
  { label: "OpenAI / GPT planner", kind: "planner", providerKey: "openai", endpointAllowlistKey: "openai-v1", modelDefault: "gpt-4o" },
];

export function ProvidersPage() {
  const { role } = useAuth();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [presetIdx, setPresetIdx] = useState(0);
  const [form, setForm] = useState({ displayName: "", modelId: "", secret: "" });
  const [formError, setFormError] = useState("");
  const [testResult, setTestResult] = useState<Record<string, string>>({});

  function load() {
    setLoading(true);
    api.get<{ providers: Provider[] }>("/providers")
      .then(r => setProviders(r.providers ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    const preset = PRESETS[presetIdx]!;
    try {
      await api.post("/providers", {
        displayName: form.displayName || preset.label,
        kind: preset.kind,
        providerKey: preset.providerKey,
        endpointAllowlistKey: preset.endpointAllowlistKey,
        modelId: form.modelId || preset.modelDefault,
        secret: form.secret,
      });
      setCreating(false);
      setForm({ displayName: "", modelId: "", secret: "" });
      load();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear proveedor");
    }
  }

  async function handleTest(id: string) {
    setTestResult(r => ({ ...r, [id]: "testing…" }));
    try {
      const res = await api.post<{ ok: boolean; latency_ms?: number; message?: string }>(`/providers/${id}/test`);
      setTestResult(r => ({ ...r, [id]: res.ok ? `✓ OK${res.latency_ms != null ? ` (${res.latency_ms}ms)` : ""}` : `✗ ${res.message ?? "fallo"}` }));
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setTestResult(r => ({ ...r, [id]: `✗ ${e.data?.message || "error"}` }));
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm("¿Revocar credenciales de este proveedor?")) return;
    try {
      await api.post(`/providers/${id}/revoke`);
      load();
    } catch { /* ignore */ }
  }

  const kindLabel = (k: string) => ({ image: "Imagen", video: "Vídeo", planner: "Planificador" }[k] || k);
  const statusColor = (s: string) => s === "active" ? "#2d6a4f" : s === "revoked" ? "#dc2626" : "#9ca3af";

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Proveedores de IA</h1>
          <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>Credenciales para generación de imágenes y vídeo</p>
        </div>
        {role === "owner" && (
          <button onClick={() => setCreating(!creating)} style={btnPrim}>+ Añadir proveedor</button>
        )}
      </div>

      <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 16px", marginBottom: 20, fontSize: 13, color: "#92400e" }}>
        OUTBOUND_ACTIONS_ENABLED=false — Las llamadas a proveedores están pausadas. Las credenciales se cifran con AES-256-GCM; la clave nunca aparece en respuestas de API.
      </div>

      {creating && role === "owner" && (
        <form onSubmit={handleCreate} style={{ background: "#fff", padding: 24, borderRadius: 10, marginBottom: 24, display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
          <h3 style={{ margin: 0 }}>Nuevo proveedor</h3>
          {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}
          <label style={lbl}>Tipo de proveedor
            <select value={presetIdx} onChange={e => setPresetIdx(Number(e.target.value))} style={inp}>
              {PRESETS.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
            </select>
          </label>
          <label style={lbl}>Nombre mostrado (opcional)
            <input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} style={inp} placeholder={PRESETS[presetIdx]!.label} />
          </label>
          <label style={lbl}>Modelo (opcional)
            <input value={form.modelId} onChange={e => setForm({ ...form, modelId: e.target.value })} style={inp} placeholder={PRESETS[presetIdx]!.modelDefault} />
          </label>
          <label style={lbl}>
            API Key (cifrada en servidor)
            <input type="password" value={form.secret} onChange={e => setForm({ ...form, secret: e.target.value })} required style={inp} placeholder="sk-… / AIza…" />
          </label>
          <div style={{ background: "#f8f6f0", borderRadius: 6, padding: "8px 12px", fontSize: 11, color: "#888" }}>
            La clave se cifra con AES-256-GCM antes de almacenarse. Solo se muestra un fingerprint SHA-256.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={btnPrim}>Guardar</button>
            <button type="button" onClick={() => setCreating(false)} style={btnSec}>Cancelar</button>
          </div>
        </form>
      )}

      {providers.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
          Sin proveedores configurados. Añade credenciales de Gemini o BFL para generar imágenes.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {providers.map(p => (
            <div key={p.id} style={{ background: "#fff", borderRadius: 10, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{p.displayName || p.providerKey}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {kindLabel(p.kind)} · {p.providerKey} · {p.modelId || "modelo por defecto"}
                    <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 10, background: statusColor(p.status) + "22", color: statusColor(p.status), fontWeight: 600, fontSize: 11 }}>{p.status}</span>
                  </div>
                  {p.secretFingerprint && (
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 4, fontFamily: "monospace" }}>
                      Fingerprint: {p.secretFingerprint}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => handleTest(p.id)} style={btnSec}>Probar</button>
                  {role === "owner" && p.status !== "revoked" && (
                    <button onClick={() => handleRevoke(p.id)} style={{ ...btnSec, color: "#dc2626", borderColor: "#fecaca" }}>Revocar</button>
                  )}
                </div>
              </div>
              {testResult[p.id] != null && (
                <div style={{
                  fontSize: 12, padding: "6px 10px", borderRadius: 6,
                  background: (testResult[p.id] ?? "").startsWith("✓") ? "#d8f3dc" : "#fef2f2",
                  color: (testResult[p.id] ?? "").startsWith("✓") ? "#2d6a4f" : "#dc2626"
                }}>{testResult[p.id]}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnPrim: React.CSSProperties = { padding: "8px 16px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#444" };
